import React, { useEffect, useRef, useState } from 'react';
import { MediaCardProps } from '../../types/video';
import { useTranslation } from 'react-i18next';

// A load error in the browser is not proof the file is gone: a dropped range
// request or a busy CDN edge fails one attempt and the next one plays. Retry
// quietly before saying anything, and never call a file the server still
// vouches for "expired".
const PREVIEW_RETRIES = 2;
const PREVIEW_RETRY_DELAY_MS = 1500;

/** A small JPG frame for a Cloudinary video, so the poster is not the whole MP4. */
export function cloudinaryVideoPoster(url: string): string | undefined {
    const match = /^(https:\/\/res\.cloudinary\.com\/[^/]+\/video\/upload\/)(.+)\.(mp4|mov|webm|m4v)(\?.*)?$/i.exec(url);
    if (!match) return undefined;
    return `${match[1]}so_0,f_jpg,w_640/${match[2]}.jpg`;
}

export const VideoCard: React.FC<MediaCardProps> = ({ item, onClick, onDelete, onEdit, onRegenerate, previewLocked = false, onUpgrade }) => {
    const { t } = useTranslation();
    const createdAt = item.createdAt ? new Date(item.createdAt) : null;
    const createdAtLabel = createdAt && !Number.isNaN(createdAt.getTime())
        ? createdAt.toLocaleDateString()
        : 'Unknown date';
    const isVideo = item.mediaType === 'video';
    const isAudio = item.mediaType === 'audio' || item.mediaType === 'voice';
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [previewError, setPreviewError] = useState(false);
    const retryTimer = useRef<number | null>(null);
    useEffect(() => () => {
        if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
    }, []);
    useEffect(() => {
        setLoadAttempt(0);
        setPreviewError(false);
    }, [item.url]);
    const mediaUnavailable = item.mediaAvailable === false || !item.url;
    const isClickable = !isVideo && !isAudio && !mediaUnavailable && !previewError;
    const mediaUrl = item.url;
    const previewUrl = item.thumbnail || item.url;
    const videoPoster = item.thumbnail && item.thumbnail !== item.url
        ? item.thumbnail
        : cloudinaryVideoPoster(item.url);
    const handlePreviewError = (event: React.SyntheticEvent<HTMLMediaElement | HTMLImageElement>) => {
        const mediaError = (event.currentTarget as HTMLMediaElement).error;
        if (loadAttempt < PREVIEW_RETRIES) {
            const next = loadAttempt + 1;
            retryTimer.current = window.setTimeout(() => setLoadAttempt(next), PREVIEW_RETRY_DELAY_MS * next);
            return;
        }
        console.warn('[gallery] preview failed to load', {
            id: item.id,
            url: item.url,
            mediaType: item.mediaType,
            code: mediaError?.code ?? null,
            message: mediaError?.message ?? null,
        });
        setPreviewError(true);
    };
    const retryPreview = () => {
        setPreviewError(false);
        setLoadAttempt((n) => n + 1);
    };
    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = mediaUrl;
        link.download = item.title || 'media';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleShare = async () => {
        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({ title: item.title, url: mediaUrl });
                return;
            } catch (error) {
                void error;
            }
        }
        await navigator.clipboard.writeText(mediaUrl);
    };
    const handleCopy = async () => {
        await navigator.clipboard.writeText(mediaUrl);
    };
    const previewOverlay = previewLocked && !mediaUnavailable && !previewError ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="rotate-[-18deg] rounded-xl border border-white/30 bg-black/45 px-4 py-2 text-xs font-bold uppercase tracking-[0.35em] text-white shadow-lg">
                {t('media.previewWatermark')}
            </div>
        </div>
    ) : null;
    return (
        <div
            className={`border rounded-lg overflow-hidden transition-shadow ${isClickable ? 'cursor-pointer hover:shadow-md' : ''}`}
            onClick={isClickable ? onClick : undefined}
        >
            <div className="relative aspect-video bg-gray-100">
                {mediaUnavailable ? (
                    <div className="w-full h-full bg-muted flex flex-col items-center justify-center text-center px-4 gap-2">
                        <div className="text-sm font-medium">{t('media.expired')}</div>
                        <div className="text-xs text-muted-foreground">
                            {item.unavailableReason ? item.unavailableReason.replace(/_/g, ' ') : t('media.expiredBody')}
                        </div>
                        {onRegenerate ? (
                            <button
                                type="button"
                                className="px-3 py-1 rounded border text-xs hover:bg-background"
                                onClick={(event) => { event.stopPropagation(); onRegenerate(item); }}
                            >
                                {t('media.reRun')}
                            </button>
                        ) : null}
                    </div>
                ) : previewError ? (
                    <div className="w-full h-full bg-muted flex flex-col items-center justify-center text-center px-4 gap-2">
                        <div className="text-sm font-medium">{t('media.previewFailed')}</div>
                        <div className="text-xs text-muted-foreground">{t('media.previewFailedBody')}</div>
                        <button
                            type="button"
                            className="px-3 py-1 rounded border text-xs hover:bg-background"
                            onClick={(event) => { event.stopPropagation(); retryPreview(); }}
                        >
                            {t('media.retryPreview')}
                        </button>
                    </div>
                ) : isVideo ? (
                    <video
                        key={loadAttempt}
                        src={mediaUrl}
                        poster={videoPoster}
                        preload="metadata"
                        playsInline
                        controls
                        className="w-full h-full object-cover"
                        onError={handlePreviewError}
                    />
                ) : isAudio ? (
                    <div className="w-full h-full flex items-center justify-center px-4">
                        <audio key={loadAttempt} src={mediaUrl} preload="metadata" controls className="w-full" onError={handlePreviewError} />
                    </div>
                ) : (
                    <img
                        key={loadAttempt}
                        src={previewUrl}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={handlePreviewError}
                    />
                )}
                {previewOverlay}
            </div>
            <div className="p-4">
                <h3 className="font-medium truncate">{item.title}</h3>
                <p className="text-sm text-gray-500">{createdAtLabel}</p>
                {previewLocked ? (
                    <p className="mt-2 text-xs text-muted-foreground">{t('media.previewLocked')}</p>
                ) : null}
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
                    <button type="button" className="px-2 py-1 rounded border hover:bg-muted disabled:opacity-50" disabled={mediaUnavailable} onClick={(event) => {
                        event.stopPropagation();
                        if (previewLocked) {
                            onUpgrade?.();
                            return;
                        }
                        handleDownload();
                    }}>{previewLocked ? t('media.unlockExport') : t('media.download')}</button>
                    <button type="button" className="px-2 py-1 rounded border hover:bg-muted disabled:opacity-50" disabled={mediaUnavailable || previewLocked} onClick={(event) => { event.stopPropagation(); void handleShare(); }}>{t('media.share')}</button>
                    <button type="button" className="px-2 py-1 rounded border hover:bg-muted disabled:opacity-50" disabled={mediaUnavailable || previewLocked} onClick={(event) => { event.stopPropagation(); void handleCopy(); }}>{t('media.copy')}</button>
                    <button type="button" className="px-2 py-1 rounded border hover:bg-muted" onClick={(event) => {
                        event.stopPropagation();
                        if (onEdit) onEdit(item);
                        else onClick();
                    }}>{t('media.edit')}</button>
                    <button type="button" className="px-2 py-1 rounded border hover:bg-muted disabled:opacity-50" disabled={mediaUnavailable} onClick={(event) => { event.stopPropagation(); onClick(); }}>{t('media.view')}</button>
                    <button type="button" className="px-2 py-1 rounded border border-red-500 text-red-500 hover:bg-red-500/10" onClick={(event) => { event.stopPropagation(); onDelete?.(item); }}>{t('media.delete')}</button>
                </div>
            </div>
        </div>
    );
};
